import { useEffect, useRef, useState } from 'react';

export interface TextFieldProps {
  value: string;
  onCommit: (next: string) => void;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  label?: string;
  type?: string;
  autoFocus?: boolean;
  /** Associates the input with a `<datalist>` for suggested values. */
  list?: string;
  autoCapitalize?: string;
}

const COMMIT_DELAY_MS = 400;

/**
 * A text input that writes through to the CRDT while you type.
 *
 * There is no save button anywhere in this app, so every field commits on a short debounce.
 * The subtlety is the other direction: a remote edit arriving mid-typing must not yank the
 * caret, so incoming values are only adopted while the field is unfocused. Once you blur,
 * the merged text — yours and theirs, both preserved — appears.
 */
export function TextField({
  value,
  onCommit,
  multiline,
  placeholder,
  className,
  label,
  type = 'text',
  autoFocus,
  list,
  autoCapitalize,
}: TextFieldProps) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current != null) clearTimeout(timer.current);
    },
    [],
  );

  const change = (next: string) => {
    setDraft(next);
    if (timer.current != null) clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(next), COMMIT_DELAY_MS);
  };

  const flush = () => {
    focused.current = false;
    if (timer.current != null) clearTimeout(timer.current);
    if (draft !== value) onCommit(draft);
  };

  const shared = {
    value: draft,
    placeholder,
    autoFocus,
    list,
    autoCapitalize,
    onFocus: () => {
      focused.current = true;
    },
    onBlur: flush,
    onChange: (event: { target: { value: string } }) => change(event.target.value),
  };

  const control = multiline ? (
    <textarea className={className ?? 'textarea'} {...shared} />
  ) : (
    <input className={className ?? 'input'} type={type} {...shared} />
  );

  if (label == null) return control;
  return (
    <div className="field">
      <span className="label">{label}</span>
      {control}
    </div>
  );
}
